import React, { useState, useEffect } from "react";
import { AutoComplete, AutoCompleteChangeEvent } from "primereact/autocomplete";
import { ReferenceEntityName } from "../../../../api/types/field-type";
import { ReferenceFieldValue } from "../types";
import { useInstitutionsSearch } from "../../../../api/queries/institutions/useInstitutionsSearch";
import { useTagList } from "../../../../api/queries/tag/list";
import { useFieldTypeSearch } from "../../../../api/queries/field-types/useFieldTypeSearch";
import { useDebounce } from "../../../../common/useDebounce";

interface RealtimeSearchStrategyProps {
  value: ReferenceFieldValue | null;
  onChange: (value: ReferenceFieldValue | null) => void;
  referenceEntity: ReferenceEntityName;
  placeholder: string;
  searchMinLength: number;
  searchDelay: number;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

export const RealtimeSearchStrategy: React.FC<RealtimeSearchStrategyProps> = ({
  value,
  onChange,
  referenceEntity,
  placeholder,
  searchMinLength,
  searchDelay,
  disabled = false,
  error = false,
  className = "",
}) => {
  const [searchOptions, setSearchOptions] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>(value?.label || "");
  const debouncedSearchQuery = useDebounce(searchQuery, searchDelay);

  const isInstitution = referenceEntity === ReferenceEntityName.Institution;
  const isTag = referenceEntity === ReferenceEntityName.Tag;
  const isField = referenceEntity === ReferenceEntityName.Field;

  const { data: institutionsResponse } = useInstitutionsSearch({
    offset: 0,
    limit: 10,
    search: isInstitution ? debouncedSearchQuery : "",
  });

  const { data: tagsResponse } = useTagList(
    isTag ? { search: debouncedSearchQuery } : { search: "" },
  );

  const { data: fieldsResponse } = useFieldTypeSearch({
    q: isField ? debouncedSearchQuery : "",
    limit: 10,
  });

  useEffect(() => {
    if (
      referenceEntity === ReferenceEntityName.Institution &&
      institutionsResponse
    ) {
      setSearchOptions(institutionsResponse.data.results);
    } else if (referenceEntity === ReferenceEntityName.Tag && tagsResponse) {
      setSearchOptions(tagsResponse.data.results);
    } else if (
      referenceEntity === ReferenceEntityName.Field &&
      fieldsResponse
    ) {
      setSearchOptions(fieldsResponse.data);
    }
  }, [referenceEntity, institutionsResponse, tagsResponse, fieldsResponse]);

  useEffect(() => {
    setSearchQuery(value?.label || "");
  }, [value]);

  const handleCompleteMethod = () => {
    // Empty function
  };

  const handleChange = (event: AutoCompleteChangeEvent) => {
    if (typeof event.value === "string") {
      setSearchQuery(event.value);
      onChange(null);
    } else if (event.value) {
      const item = event.value;
      const newValue = {
        id: item.id,
        label: item.name,
      };
      setSearchQuery(item.name);
      onChange(newValue);
    } else {
      setSearchQuery("");
      onChange(null);
    }
  };

  return (
    <AutoComplete
      value={searchQuery}
      suggestions={searchOptions}
      completeMethod={handleCompleteMethod}
      onChange={handleChange}
      field="name"
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full ${error ? "p-invalid" : ""} ${className}`}
    />
  );
};
